import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Building2, CheckCircle, KeyRound, Lock, Smartphone } from "lucide-react";
import { authAPI, getApiErrorPayload } from "@/lib/api";
import { isValidSaudiMobile } from "@/lib/saudi-phone";
import type { LoginCompanyChoice } from "@/features/auth/contexts/AuthContext";

type Step = "phone" | "code" | "done";

const fieldClass = "h-12 border border-input bg-card text-primary placeholder:text-muted-foreground";
const iconClass = "absolute start-3 top-1/2 -translate-y-1/2 text-primary w-4 h-4";

/**
 * Password reset by SMS: the mobile number gets a 6-digit code (valid 10 minutes), then the code and
 * a new password are submitted together. Outside production the API returns the code, shown here,
 * because no SMS provider is connected yet.
 */
export const ForgotPasswordPage: React.FC = () => {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [step, setStep] = useState<Step>("phone");
	const [isLoading, setIsLoading] = useState(false);
	const [phone, setPhone] = useState("");
	const [devCode, setDevCode] = useState<string | null>(null);
	const [code, setCode] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [errors, setErrors] = useState<Partial<Record<"phone" | "code" | "newPassword" | "confirmPassword", string>>>(
		{},
	);
	// Filled when the code is valid for accounts in several companies.
	const [companies, setCompanies] = useState<LoginCompanyChoice[] | null>(null);
	const [companyId, setCompanyId] = useState<number | null>(null);

	useEffect(() => {
		setCompanies(null);
		setCompanyId(null);
	}, [code]);

	const showError = (error: unknown) => {
		const payload = getApiErrorPayload(error);
		toast({
			title: t("common.error"),
			description: t(payload.key, { defaultValue: t("common.unexpectedError") }),
			variant: "destructive",
		});
	};

	const requestCode = async (event?: React.FormEvent) => {
		event?.preventDefault();
		const trimmed = phone.trim();
		if (!trimmed || !isValidSaudiMobile(trimmed)) {
			setErrors({ phone: t(trimmed ? "auth.validation.phoneInvalid" : "auth.validation.phoneRequired") });
			return;
		}
		setErrors({});
		setIsLoading(true);
		try {
			const result = await authAPI.forgotPassword(trimmed);
			setDevCode(result.devCode ?? null);
			setCode("");
			setStep("code");
		} catch (error) {
			showError(error);
		} finally {
			setIsLoading(false);
		}
	};

	const submitReset = async (event: React.FormEvent) => {
		event.preventDefault();
		const next: typeof errors = {};
		if (!/^\d{6}$/.test(code.trim())) next.code = t("auth.errors.resetCodeFormat");
		if (newPassword.length < 8) next.newPassword = t("auth.errors.newPasswordMinLength");
		else if (newPassword !== confirmPassword) next.confirmPassword = t("auth.errors.passwordsDoNotMatch");
		setErrors(next);
		if (Object.keys(next).length > 0) return;
		if (companies && companyId == null) return;

		setIsLoading(true);
		try {
			await authAPI.resetPassword({
				phone: phone.trim(),
				code: code.trim(),
				newPassword,
				confirmPassword,
				...(companyId != null ? { companyId } : {}),
			});
			setStep("done");
		} catch (error) {
			const choices = (error as { response?: { data?: { companies?: unknown } } })?.response?.data?.companies;
			if (Array.isArray(choices)) {
				setCompanies(choices as LoginCompanyChoice[]);
				setCompanyId(null);
			} else {
				showError(error);
			}
		} finally {
			setIsLoading(false);
		}
	};

	if (step === "done") {
		return (
			<div className="min-h-screen flex items-center justify-center bg-card p-4 ">
				<div className="w-full max-w-md">
					<Card className="shadow-card">
						<CardHeader className="text-center space-y-2">
							<div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-4">
								<CheckCircle className="w-8 h-8 text-success" />
							</div>
							<CardTitle className="text-base font-bold text-foreground">
								{t("auth.passwordChangedTitle")}
							</CardTitle>
							<CardDescription className="text-muted-foreground">
								{t("auth.passwordChangedBody")}
							</CardDescription>
						</CardHeader>
						<CardContent className="text-center">
							<Link to="/login">
								<Button className="w-full">{t("auth.backToLogin")}</Button>
							</Link>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-card">
			<div className="w-full flex items-center justify-center flex-col gap-8">
				<div className="flex items-center justify-center flex-col gap-4 w-full">
					<img src="/images/logo.png" alt="logo" className="w-24 h-24 mx-auto" />
					<p className="text-xl font-bold text-primary">{t("auth.pos")}</p>
				</div>
				<div className="w-full items-center max-w-md justify-center px-4">
					<Card className="shadow-card w-full border border-input p-6 flex flex-col rounded-3xl ">
						<CardHeader className="space-y-2 mb-4 text-start px-0">
							<CardTitle className="text-base font-bold text-foreground">
								{t("auth.forgotPassword")}
							</CardTitle>
							<CardDescription className="text-foreground">
								{step === "phone"
									? t("auth.enterPhoneForReset")
									: t("auth.resetCodeSent", { phone: phone.trim() })}
							</CardDescription>
						</CardHeader>

						<CardContent className="p-0">
							{step === "phone" ? (
								<form onSubmit={requestCode} className="space-y-4" noValidate>
									<div className="space-y-2">
										<label htmlFor="phone" className="text-sm font-medium text-foreground">
											{t("auth.phone")}
										</label>
										<div className="relative">
											<Smartphone className={iconClass} />
											<Input
												id="phone"
												type="tel"
												inputMode="tel"
												autoComplete="username"
												dir="ltr"
												placeholder={t("auth.phonePlaceholder")}
												className={`px-10 rtl:text-end ltr:text-start ${fieldClass}`}
												aria-invalid={!!errors.phone}
												value={phone}
												onChange={e => setPhone(e.target.value)}
											/>
										</div>
										{errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
									</div>

									<Button type="submit" className="w-full mt-4 h-12 font-bold" disabled={isLoading}>
										{isLoading ? t("auth.sending") : t("auth.sendResetCode")}
									</Button>
								</form>
							) : (
								<form onSubmit={submitReset} className="space-y-4" noValidate>
									{devCode && (
										<p
											className="rounded-md bg-card/10 px-3 py-2 text-sm text-foreground"
											dir="auto"
										>
											{t("auth.devResetCode", { code: devCode })}
										</p>
									)}
									<div className="space-y-2">
										<label htmlFor="code" className="text-sm font-medium text-foreground">
											{t("auth.resetCode")}
										</label>
										<div className="relative">
											<KeyRound className={iconClass} />
											<Input
												id="code"
												inputMode="numeric"
												autoComplete="one-time-code"
												maxLength={6}
												dir="ltr"
												placeholder="000000"
												className={`px-10 tracking-widest rtl:text-end ltr:text-start ${fieldClass}`}
												aria-invalid={!!errors.code}
												value={code}
												onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
											/>
										</div>
										{errors.code && <p className="text-sm text-destructive">{errors.code}</p>}
									</div>

									<div className="space-y-2">
										<label htmlFor="newPassword" className="text-sm font-medium text-foreground">
											{t("auth.newPassword")}
										</label>
										<div className="relative">
											<Lock className={iconClass} />
											<Input
												id="newPassword"
												type="password"
												autoComplete="new-password"
												className={`ps-10 ${fieldClass}`}
												aria-invalid={!!errors.newPassword}
												value={newPassword}
												onChange={e => setNewPassword(e.target.value)}
											/>
										</div>
										{errors.newPassword && (
											<p className="text-sm text-destructive">{errors.newPassword}</p>
										)}
									</div>

									<div className="space-y-2">
										<label
											htmlFor="confirmPassword"
											className="text-sm font-medium text-foreground"
										>
											{t("auth.confirmNewPassword")}
										</label>
										<div className="relative">
											<Lock className={iconClass} />
											<Input
												id="confirmPassword"
												type="password"
												autoComplete="new-password"
												className={`ps-10 ${fieldClass}`}
												aria-invalid={!!errors.confirmPassword}
												value={confirmPassword}
												onChange={e => setConfirmPassword(e.target.value)}
											/>
										</div>
										{errors.confirmPassword && (
											<p className="text-sm text-destructive">{errors.confirmPassword}</p>
										)}
									</div>

									{companies && (
										<fieldset className="space-y-2">
											<legend className="text-sm font-medium text-foreground">
												{t("auth.errors.selectCompany")}
											</legend>
											{companies.map(company => (
												<label
													key={company.id}
													className="flex h-12 cursor-pointer items-center gap-3 rounded-md bg-card px-3 text-primary"
												>
													<input
														type="radio"
														name="companyId"
														value={company.id}
														checked={companyId === company.id}
														onChange={() => setCompanyId(company.id)}
													/>
													<Building2 className="w-4 h-4 shrink-0" />
													<span className="truncate text-sm">
														{company.name ?? `#${company.id}`}
													</span>
												</label>
											))}
										</fieldset>
									)}

									<Button
										type="submit"
										className="w-full mt-4 h-12 font-bold"
										disabled={isLoading || (companies != null && companyId == null)}
									>
										{isLoading ? t("auth.saving") : t("auth.setNewPassword")}
									</Button>

									<div className="flex items-center justify-between gap-3 text-sm">
										<button
											type="button"
											className="text-foreground hover:underline"
											onClick={() => {
												setStep("phone");
												setDevCode(null);
												setErrors({});
											}}
										>
											{t("auth.changePhone")}
										</button>
										<button
											type="button"
											className="text-foreground hover:underline disabled:opacity-60"
											disabled={isLoading}
											onClick={() => void requestCode()}
										>
											{t("auth.resendCode")}
										</button>
									</div>
								</form>
							)}

							<div className="mt-6 text-center">
								<Link
									to="/login"
									className="inline-flex items-center text-sm text-foreground hover:text-foreground/80 hover:underline"
								>
									<ArrowLeft className="w-4 h-4 mx-1 rtl:rotate-180" />
									{t("auth.backToLogin")}
								</Link>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
};
